import React from 'react';
import { motion } from 'framer-motion';
import { Lock, AlertTriangle, Zap, Check, Lightbulb, Users, Sparkles, ArrowRight } from 'lucide-react';

function ProblemSolutionSection() {
    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { 
            opacity: 1,
            transition: { 
                staggerChildren: 0.2
            }
        }
    };
    
    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: { duration: 0.5 }
        }
    };

    // Problem data with enhanced descriptions
    const closedInnovationProblems = [
        {
            title: "Intellectual Property Silos",
            description: "Companies hoard IP behind legal walls, preventing knowledge transfer and slowing global innovation.",
            icon: <Lock size={16} />
        },
        {
            title: "Patents & NDAs Create Barriers",
            description: "Legal protection mechanisms restrict knowledge sharing and collaboration across organizational boundaries.",
            icon: <AlertTriangle size={16} />
        },
        {
            title: "Redundant Work & Resource Waste",
            description: "Multiple teams solve identical problems in isolation, wasting time and capital on duplicate efforts.",
            icon: <Zap size={16} />
        },
        {
            title: "Misaligned Incentives",
            description: "Researchers and developers often lack ownership of their work, reducing motivation and innovation quality.",
            icon: <Users size={16} />
        }
    ];

    const openInnovationProblems = [
        {
            title: "Unsustainable Funding Models",
            description: "Hackathons and grants provide only temporary resources, leading to project abandonment.",
            icon: <Zap size={16} />
        },
        {
            title: "Ownership Attribution Challenges",
            description: "Lack of trustless ownership mechanisms creates disputes over contributions and recognition.",
            icon: <Users size={16} />
        },
        {
            title: "Inefficient Coordination",
            description: "Traditional project management tools break down with distributed, global collaboration.",
            icon: <AlertTriangle size={16} />
        },
        {
            title: "Limited Investment Liquidity",
            description: "Investors can't easily enter or exit open-source projects, constraining available capital.",
            icon: <Lock size={16} />
        }
    ];

    // Solution data in structured format to match problem data
    const solutionItems = [
        {
            title: "Trustless Collaboration Mechanism",
            description: "Decentralized task management ensures fair contribution tracking and reward distribution.",
            icon: <Users size={16} />
        },
        {
            title: "Innovation Tokenization",
            description: "Fractional ownership with Innovation Units (IUs) makes projects investable at any scale.",
            icon: <Sparkles size={16} />
        },
        {
            title: "Aligned Incentives",
            description: "All participants share the same rewards structure, aligning contributors, investors, and users for mutual success.",
            icon: <Check size={16} />
        },
        {
            title: "Task-Based Contribution Economy",
            description: "Structured missions and tasks drive project development organically with clear reward paths.",
            icon: <Lightbulb size={16} />
        },
        {
            title: "Decentralized Investment Gateway",
            description: "Open investment model for funding innovation with transparent ownership rights and liquidity.",
            icon: <Zap size={16} />
        },
        {
            title: "Autonomous Proof-of-Impact Incentives",
            description: "Automatic rewards based on measurable project impact and contribution value.",
            icon: <Check size={16} />
        },
        {
            title: "Decentralized Project Management",
            description: "Community-driven development with transparent governance and progress tracking.",
            icon: <Users size={16} />
        },
        {
            title: "Community Trust Signals",
            description: "Stakers fuel project growth by signaling trust and boosting reward emissions through evaluation of trusted projects.",
            icon: <Sparkles size={16} />
        }
    ];

    return (
        <section className="py-16 px-4 md:px-8 w-full bg-gray-50 relative overflow-hidden">
            {/* Background Design Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute -top-[10%] -right-[5%] w-[40%] h-[40%] rounded-full bg-indigo-50 filter blur-[100px] opacity-70"></div>
                <div className="absolute top-[60%] -left-[5%] w-[30%] h-[30%] rounded-full bg-purple-50 filter blur-[100px] opacity-70"></div>
            </div>
            
            <div className="relative z-10">
                {/* Main Title */}
                <motion.div 
                    className="mb-16 text-center"
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                >
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                        Why Traditional Innovation Infrastructure is Broken
                    </h1>
                    <div className="h-1 w-20 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 mx-auto rounded-full"></div>
                </motion.div>
                
                {/* Modified Structure: First a horizontal section with intro text, then problems and solutions side by side */}
                <div className="max-w-7xl mx-auto">
                    {/* Introduction section with two-part explanation */}
                    <motion.div 
                        className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {/* Problem Intro */}
                        <motion.div
                            className="text-center lg:text-left"
                            variants={itemVariants}
                        >
                            <h2 className="text-3xl font-bold mb-4 text-[#BE5B50]">Innovation is NOT Scalable & Sustainable</h2>
                            <p className="text-gray-600 text-lg leading-relaxed">
                                Current innovation models are stuck between two flawed extremes, 
                                both unable to meet the demands of modern collaborative development.
                            </p>
                        </motion.div>

                        {/* Solution Intro */}
                        <motion.div
                            className="text-center"
                            variants={itemVariants}
                        >
                            <h2 className="text-3xl font-bold mb-4 text-indigo-600">How CelystikHub Fixes It</h2>
                            <p className="text-gray-600 text-lg leading-relaxed">
                                CelystikHub bridges the gap by providing a trustless infrastructure that enables transparent collaboration with sustainable incentives.
                            </p>
                        </motion.div>
                    </motion.div>
                    
                    {/* Modified layout for problems and solutions */}
                    <div className="flex flex-col gap-8 relative">
                        {/* Bridge indicator for mobile view */}
                        <div className="lg:hidden w-full py-6 relative">
                            <div className="h-[2px] w-full bg-gradient-to-r from-red-500 via-purple-500 to-indigo-500"></div>
                            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg">
                                <span className="text-xs font-bold text-indigo-600">BRIDGES THE GAP</span>
                            </div>
                        </div>
                        
                        {/* Content Row 1: Closed Innovation Problem and First Solutions */}
                        <div className="flex flex-col lg:flex-row gap-8 relative">
                            {/* Closed Innovation Problem */}
                            <motion.div 
                                className="w-full lg:w-[46%]"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {/* Closed Innovation */}
                                <motion.div 
                                    className="rounded-2xl p-6 md:p-8 bg-white border border-gray-200 shadow-md"
                                    variants={itemVariants}
                                >
                                    <div className="flex items-center mb-4">
                                        <div className="w-12 h-12 rounded-xl bg-[#BE5B50] flex items-center justify-center shadow-md mr-4">
                                            <Lock size={24} className="text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold text-gray-800">Closed Innovation</h3>
                                            <p className="text-black text-sm md:text-base">Corporate R&D model that stifles collaboration</p>
                                        </div>
                                    </div>

                                    <ul className="space-y-4 mt-6">
                                        {closedInnovationProblems.map((item, index) => (
                                            <motion.li 
                                                key={index}
                                                className="flex items-start bg-red-50 p-3 rounded-lg border border-red-100 hover:border-red-200 transition-all duration-300"
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.1 * index, duration: 0.5 }}
                                                whileHover={{ x: 5 }}
                                            >
                                                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#D29F80] flex items-center justify-center shadow-md mr-3">
                                                    <div className="text-white">
                                                        {item.icon}
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-gray-800 text-md">{item.title}</h4>
                                                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">{item.description}</p>
                                                </div>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </motion.div>
                            </motion.div>

                            {/* Center Bridge - Desktop View */}
                            <div className="hidden lg:flex lg:w-[8%] relative justify-center items-center">
                                <div className="h-full w-[2px] bg-gradient-to-b from-red-500 via-purple-500 to-indigo-500"></div>
                                <div className="absolute  transform -translate-y-1/2">
                                    <motion.div
                                        className="w-12 h-12 rounded-full bg-white border-4 border-purple-500 flex items-center justify-center shadow-lg shadow-purple-200"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: 0.5, duration: 0.5 }}
                                    >
                                        <Sparkles size={20} className="text-purple-600" />
                                    </motion.div>
                                </div>
                            </div>

                            {/* First Half of Solutions */}
                            <motion.div 
                                className="w-full lg:w-[46%]"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                <motion.div 
                                    className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200 shadow-md"
                                    variants={itemVariants}
                                >
                                    <div className="flex items-center mb-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-md mr-4">
                                            <Zap size={24} className="text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold text-gray-800">CelystikHub Solutions</h3>
                                            <p className="text-indigo-600 text-sm md:text-base">Innovative infrastructure for better collaboration</p>
                                        </div>
                                    </div>
                                    
                                    <ul className="space-y-4 mt-6">
                                        {solutionItems.slice(0, 4).map((item, index) => (
                                            <motion.li 
                                                key={index}
                                                className="flex items-start bg-indigo-50 p-3 rounded-lg border border-indigo-100 hover:border-indigo-200 transition-all duration-300"
                                                initial={{ opacity: 0, x: 10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.1 * index, duration: 0.5 }}
                                                whileHover={{ x: -5 }}
                                            >
                                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center shadow-md mr-3">
                                                    <div className="text-white">
                                                        {item.icon}
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-gray-800 text-md">{item.title}</h4>
                                                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">{item.description}</p>
                                                </div>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </motion.div>
                            </motion.div>
                        </div>

                        {/* Content Row 2: Open Innovation Problem and Remaining Solutions */}
                        <div className="flex flex-col lg:flex-row gap-8 relative">
                            {/* Open Innovation Problem */}
                            <motion.div 
                                className="w-full lg:w-[46%]"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {/* Open Innovation */}
                                <motion.div 
                                    className="rounded-2xl p-6 md:p-8 bg-white border border-gray-200 shadow-md"
                                    variants={itemVariants}
                                >
                                    <div className="flex items-center mb-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-md mr-4">
                                            <AlertTriangle size={24} className="text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold text-gray-800">Open Innovation</h3>
                                            <p className="text-amber-500 text-sm md:text-base">Lacks resources and coordination mechanisms</p>
                                        </div>
                                    </div>

                                    <ul className="space-y-4 mt-6">
                                        {openInnovationProblems.map((item, index) => (
                                            <motion.li 
                                                key={index}
                                                className="flex items-start bg-amber-50 p-3 rounded-lg border border-amber-100 hover:border-amber-200 transition-all duration-300"
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.1 * index, duration: 0.5 }}
                                                whileHover={{ x: 5 }}
                                            >
                                                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-md mr-3">
                                                    <div className="text-white">
                                                        {item.icon}
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-gray-800 text-md">{item.title}</h4>
                                                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">{item.description}</p>
                                                </div>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </motion.div>
                            </motion.div>

                            {/* Center Bridge - Desktop View */}
                            <div className="hidden lg:flex lg:w-[8%] relative justify-center items-center">
                                <div className="h-full w-[2px] bg-gradient-to-b from-amber-500 via-purple-500 to-indigo-500"></div>
                                <div className="absolute  transform -translate-y-1/2">
                                    <motion.div
                                        className="w-12 h-12 rounded-full bg-white border-4 border-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-200"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: 0.7, duration: 0.5 }}
                                    >
                                        <Check size={20} className="text-indigo-600" />
                                    </motion.div>
                                </div>
                            </div>

                            {/* Second Half of Solutions */}
                            <motion.div 
                                className="w-full lg:w-[46%]"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                <motion.div 
                                    className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200 shadow-md"
                                    variants={itemVariants}
                                >
                                    <div className="flex items-center mb-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shadow-md mr-4">
                                            <Lightbulb size={24} className="text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold text-gray-800">Advanced Features</h3>
                                            <p className="text-purple-600 text-sm md:text-base">Powerful tools for next-gen collaboration</p>
                                        </div>
                                    </div>
                                    
                                    <ul className="space-y-4 mt-6">
                                        {solutionItems.slice(4).map((item, index) => (
                                            <motion.li 
                                                key={index}
                                                className="flex items-start bg-purple-50 p-3 rounded-lg border border-purple-100 hover:border-purple-200 transition-all duration-300"
                                                initial={{ opacity: 0, x: 10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.1 * index, duration: 0.5 }}
                                                whileHover={{ x: -5 }}
                                            >
                                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center shadow-md mr-3">
                                                    <div className="text-white">
                                                        {item.icon}
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-gray-800 text-md">{item.title}</h4>
                                                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">{item.description}</p>
                                                </div>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </motion.div>
                            </motion.div>
                        </div>
                        
                        {/* CTA Button */}
                        {/* <motion.div 
                            className="mt-8 flex justify-center"
                            variants={itemVariants}
                        >
                            <button className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-indigo-200 transition duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transform hover:-translate-y-1">
                                <div className="flex items-center">
                                    <span>Explore How It Works</span>
                                    <ArrowRight size={18} className="ml-2" />
                                </div>
                            </button>
                        </motion.div> */}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default ProblemSolutionSection; 